import styled, { css } from "styled-components";
import React, { useState, useEffect, useCallback } from "react";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useEmblaCarousel } from "embla-carousel/react";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";

type ProductModel = {
    fields: {
        productImage: string;
        productTitle: string;
        productLink: string;
        linkText: string;
    };
}

type Props = {
    fields: {
        leftButtonImage: string;
        rightButtonImage: string;
        products: ProductModel[];
    };
};

const ProductImage = styled.div`
    width: 145px;
    height: 145px;
    background-color: #e0e0e0;
`;

const dividerStyles = {
    css: css`
        border-top: 3px solid #016EAF;
        width:100%;
        margin-bottom: 2rem;
        margin-top: 1rem;
    `
}

const productDivStyles = {
    css: css`
        text-align: center;
        margin-left: 1rem;
        margin-right: 1rem;
    `
}

const productTitleStyles = {
    css: css`
        height: 4rem;
    `
}

const productLinkStyles = {
    css: css`
        font-weight: bold;
        color: #016EAF;
    `
}

const productCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        align: "middle" as const,
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        align: "middle" as const,
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            min-width: 33%
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
};

const styles = productCarouselStyles;


const emblaStyle = {
    css: css`
        display: flex;
    `
}

const embla__viewport = {
    css: css`
    overflow: hidden;
    width: 100%;
    `
}

const embla__container = {
    css: css`
        display: flex;
        min-height: 400px;
    `
}

const embla__slide = {
    css: css`
        min-width: 33.33%;
        text-align: center;
        padding: 1rem;
    `
}

const embla__slide__inner = {
    css: css`
        position: relative;
        overflow: hidden;
    `
}


const HomeCarousel: React.FC<Props> = ({ fields: { products, leftButtonImage, rightButtonImage } }: Props) => {
    const [viewportRef, embla] = useEmblaCarousel({ slidesToScroll: 3 });
    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

    const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
    const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);
    const onSelect = useCallback(() => {
        if (!embla) return;
        setPrevBtnEnabled(embla.canScrollPrev());
        setNextBtnEnabled(embla.canScrollNext());
    }, [embla]);

    useEffect(() => {
        if (!embla) return;
        embla.on("select", onSelect);
        onSelect();
    }, [embla, onSelect]);

    return (
        <StyledWrapper {...emblaStyle}>

            <GridItem {...styles.prevArrowGridItem}>
                <Button
                    {...styles.prevArrowButton}
                    onClick={scrollPrev}
                    disabled={!prevBtnEnabled}
                    data-test-selector="prevBtn"
                >
                    <ButtonIcon src={ChevronLeft} />
                </Button>

            </GridItem>

            <StyledWrapper {...embla__viewport} ref={viewportRef}>
                <StyledWrapper {...embla__container}>
                    {products.map((product, index) => (
                        <StyledWrapper {...embla__slide} key={index}>
                            <StyledWrapper {...embla__slide__inner}>
                                <img src={product.fields.productImage} alt="" width="100%" height="320px" />
                                <StyledWrapper {...dividerStyles}></StyledWrapper>
                                <StyledWrapper {...productTitleStyles}>{product.fields.productTitle}</StyledWrapper>
                                <StyledWrapper {...productLinkStyles}>
                                    <a href={product.fields.productLink}>{product.fields.linkText}</a>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </StyledWrapper>

            <GridItem {...styles.nextArrowGridItem}>
                <Button
                    {...styles.nextArrowButton}
                    onClick={scrollNext}
                    disabled={!nextBtnEnabled}
                    data-test-selector="nextBtn"
                >
                    <ButtonIcon src={ChevronRight} />
                </Button>

            </GridItem>
        </StyledWrapper>

    )
}

const widgetModule: WidgetModule = {
    component: HomeCarousel,
    definition: {
        fieldDefinitions: [
            {
                name: "leftButtonImage",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                displayName: "Left Button Image",
                defaultValue: "",
            },
            {
                name: "rightButtonImage",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                displayName: "Right Button Image",
                defaultValue: "",
            },
            {
                name: "products",
                editorTemplate: "ListField",
                //tab: contentTab,
                getDisplay: ({ fields: { productTitle, productLink } }) => productTitle || productLink || "Slide",
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "productImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imageSize",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "productLink",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "productTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "linkText",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    }
                ],
            },
        ],
        group: "Common",
        icon: "Carousel",
    }
}

export default widgetModule;